import { startTransition, useCallback, useEffect, useState } from 'react';

import { Button } from '@flintmint/web-shared/components/button';
import { Checkbox } from '@flintmint/web-shared/components/checkbox';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';
import type { ModuleType } from '@flintmint/core-shared/firestore/schemas';

import { CollapsibleSection } from '@/components/collapsible-section';
import type { FlowWithId } from '@/services/flows-service';
import {
  updateModule,
  deleteModule,
  createModule,
  type MedicalModuleInput,
  type MedicalModuleWithId,
} from '@/services/medical-modules-service';
import { ModuleRow } from '../module-row/module-row';
import { AddModuleRow } from '../add-module-row/add-module-row';

import styles from './module-section.module.scss';

const INITIAL_ROW_COUNT = 12;

export const ModuleSection = ({
  types,
  label,
  modules,
  moduleIdsByName,
  totalCount,
  flows,
  isLoading,
  selectedIds,
  onToggleSelect,
  onToggleSelectMany,
}: ModuleSectionProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_ROW_COUNT);
  const userEmail = getAuthUser().email ?? '';
  const showType = types.length > 1;

  const { showToastNotification } = useToaster();

  // Mounting every row (each with its popovers) in one commit delays the first paint, so rows past the first batch
  // mount in a transition. The count only grows: `modules` is a new array on every dashboard render, and shrinking
  // would remount the later rows and drop their edit state.
  useEffect(() => {
    if (modules.length > visibleCount) {
      startTransition(() => setVisibleCount(modules.length));
    }
  }, [modules.length, visibleCount]);

  // Each handler reports its own outcome. A save also resolves to whether it succeeded, so the row
  // keeps its edit form open after a failure.
  const handleAdd = async (data: MedicalModuleInput) => {
    try {
      await createModule(data);
      showToastNotification({ type: 'success', title: 'Module created', description: data.name });
      setIsAdding(false);
    } catch {
      showToastNotification({ type: 'error', title: 'Failed to create module', description: data.name });
    }
  };

  const handleSave = useCallback(
    async (module: MedicalModuleWithId, data: Partial<MedicalModuleInput>): Promise<boolean> => {
      try {
        await updateModule(module.id, data, userEmail);
        showToastNotification({ type: 'success', title: savedTitle(data), description: data.name ?? module.name });
        return true;
      } catch {
        showToastNotification({ type: 'error', title: 'Failed to update module', description: module.name });
        return false;
      }
    },
    [userEmail, showToastNotification]
  );

  const handleDelete = useCallback(
    async (module: MedicalModuleWithId) => {
      try {
        await deleteModule(module.id);
        showToastNotification({ type: 'success', title: 'Module deleted', description: module.name });
      } catch {
        showToastNotification({ type: 'error', title: 'Failed to delete module', description: module.name });
      }
    },
    [showToastNotification]
  );

  const renderContent = () => {
    if (isLoading) {
      return <LoadingSkeleton showType={showType} />;
    }

    return (
      <>
        {isAdding && (
          <AddModuleRow
            availableTypes={types}
            flows={flows}
            moduleIdsByName={moduleIdsByName}
            onAdd={handleAdd}
            onCancel={() => setIsAdding(false)}
            showType={showType}
          />
        )}

        {modules.slice(0, visibleCount).map((module) => (
          <ModuleRow
            key={module.id}
            module={module}
            flows={flows}
            moduleIdsByName={moduleIdsByName}
            onSave={handleSave}
            onDelete={handleDelete}
            selected={selectedIds.has(module.id)}
            onToggleSelect={onToggleSelect}
            showType={showType}
            availableTypes={types}
          />
        ))}

        {renderEmptyState()}
      </>
    );
  };

  const renderEmptyState = () => {
    if (isAdding) return null;

    if (totalCount === 0) {
      return <div className={styles.emptyState}>No {label.toLowerCase()}</div>;
    }

    if (modules.length === 0) {
      return <div className={styles.emptyState}>No matching {label.toLowerCase()}</div>;
    }

    return null;
  };

  const title = (
    <>
      {label} <span className={styles.count}>({modules.length})</span>
    </>
  );

  const actions = (
    <Button color="dark" appearance="outline" onClick={() => setIsAdding(true)} disabled={isAdding}>
      + Add
    </Button>
  );

  const selectedCount = modules.filter((module) => selectedIds.has(module.id)).length;

  const contentHeader = (
    <div className={styles.tableHeader} data-has-type={showType || undefined}>
      <Checkbox
        checked={modules.length > 0 && selectedCount === modules.length}
        intermediate={selectedCount > 0 && selectedCount < modules.length}
        disabled={modules.length === 0}
        onChange={() => onToggleSelectMany(modules.map((module) => module.id))}
        aria-label={`Select all ${label.toLowerCase()}`}
        data-testid="module-section-select-all"
      />
      <span>Name</span>
      {showType && <span>Type</span>}
      <span>Criteria</span>
      <span>Content</span>
      <span>Flows</span>
      <span>Enabled</span>
      <span>Updated</span>
      <span>Actions</span>
    </div>
  );

  return (
    <CollapsibleSection title={title} actions={actions} contentHeader={contentHeader} stickyChromeTone="subtle">
      <div className={styles.tableBody} aria-busy={visibleCount < modules.length}>
        {renderContent()}
      </div>
    </CollapsibleSection>
  );
};

// The row sends one kind of change at a time: the enabled switch, the flow chips, or the edit form.
const savedTitle = (data: Partial<MedicalModuleInput>): string => {
  if (Object.keys(data).length > 1) return 'Module saved';
  if (data.enabled !== undefined) return data.enabled ? 'Module enabled' : 'Module disabled';
  if (data.flowIds) return 'Module flows updated';
  return 'Module saved';
};

const LoadingSkeleton = ({ showType }: { showType?: boolean }) => (
  <>
    {[0, 1].map((i) => (
      <div key={i} className={styles.row} data-has-type={showType || undefined} data-testid="module-row-skeleton">
        <span />
        <Skeleton width={150} height={24} />
        {showType && <Skeleton width={80} height={24} />}
        <Skeleton width={180} height={24} />
        <Skeleton width={200} height={24} />
        <Skeleton width={120} height={24} />
        <Skeleton width={32} height={20} />
        <Skeleton width={100} height={24} />
        <Skeleton width={120} height={24} />
      </div>
    ))}
  </>
);

interface ModuleSectionProps {
  types: ModuleType[];
  label: string;
  /** Modules of this section's types that match the dashboard filters. */
  modules: MedicalModuleWithId[];
  /** Every module of every type by name, unfiltered; module names are unique across all of them. */
  moduleIdsByName: ReadonlyMap<string, readonly string[]>;
  /** Count before filtering, to tell an empty section from one with no matches. */
  totalCount: number;
  flows: FlowWithId[];
  isLoading: boolean;
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectMany: (ids: string[]) => void;
}
